import {
  Authentication,
  renderFormattedDate,
  sortBooksByCompanyName,
  prepareActiveUserOptions,
  renderDataGridLocaleText,
  renderAutoCompleteLocaleText,
} from 'utils';
import {
  DataGridBox,
  CustomToolbar,
  ZebraDataGrid,
  CrudActionButton,
  ToolbarFilterRow,
  PermissionHandler,
  CustomNoRowsOverlay,
  DateRangeChipsArray,
  GridFilterDateInput,
  type DateRangeFilter,
  getThisMonthDateRange,
} from 'components';
import {
  DetailDrawer,
  CreateActivityBoardItemDialog,
  UpdateActivityBoardItemDialog,
  DeleteActivityBoardItemDialog,
} from './components';
import {
  GridColDef,
  useGridApiRef,
  GridColTypeDef,
  GRID_DATE_COL_DEF,
  isAutogeneratedRow,
  GridActionsCellItem,
  getGridDateOperators,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import { Book } from 'pages/dashboard';
import { Project } from 'library/types';
import { useSnackbar } from 'notistack';
import { isLastDayOfMonth } from './utils';
import apiService from 'library/api-service';
import { TABLE_SCHEMA_PAGE_NAME_ENUM } from 'enums';
import { gray } from 'providers/theme/themePrimitives';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useLookup, useSingleRowSelectionGuard } from 'hooks';
import projectsActions from 'store/features/projects/actions';
import { ActivityBoardItem, ActivityType, User } from 'library/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { Alert, useTheme, TextField, Autocomplete, Grid2 as Grid, svgIconClasses } from '@mui/material';

const dateColumnType: GridColTypeDef<Date, string> = {
  ...GRID_DATE_COL_DEF,
  resizable: false,
  filterOperators: getGridDateOperators(false).map((item) => ({
    ...item,
    InputComponent: GridFilterDateInput,
    InputComponentProps: { showTime: false },
  })),
};

const DEFAULT_DATE_CHIP_KEY = 'thisMonth';

const loadActiveUsers = async () => {
  const response = await apiService.getUsers();

  return { ...response, data: response.isSuccess ? prepareActiveUserOptions(response.data) : [] };
};

const loadSortedBooks = async () => {
  const response = await apiService.getBooks();

  return { ...response, data: response.isSuccess ? sortBooksByCompanyName(response.data) : [] };
};

const ActivityManaging = () => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const { enqueueSnackbar } = useSnackbar();
  const requireSingleRowSelection = useSingleRowSelectionGuard();
  const { items: users } = useLookup<User>(loadActiveUsers);
  const { items: books } = useLookup<Book>(loadSortedBooks);
  const { items: activityTypes } = useLookup<ActivityType>(apiService.getActivityTypes);
  const pageName = TABLE_SCHEMA_PAGE_NAME_ENUM.ACTIVITY_MANAGING;
  const activityBoardItemsRequestIdRef = useRef(0);

  // redux hooks
  const dispatch = useAppDispatch();
  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [detailRow, setDetailRow] = useState<ActivityBoardItem | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Project | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeFilter>(getThisMonthDateRange);
  const [selectedDateChipKey, setSelectedDateChipKey] = useState(DEFAULT_DATE_CHIP_KEY);
  const [activityBoardItems, setActivityBoardItems] = useState<ActivityBoardItem[]>([]);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  const showMonthEndOvertimeReminder = isLastDayOfMonth();

  const selectedRow = useMemo(
    () => activityBoardItems.find((row) => row.id === rowSelectionModel[0]) ?? null,
    [activityBoardItems, rowSelectionModel]
  );

  const filteredActivityTypes = useMemo(() => {
    const me = users.find((user) => user.nameSurname === Authentication.getLoggedUser());

    if (!me?.roleId) return activityTypes;

    return activityTypes.filter((at) => at.roles.some((role) => role.id === me.roleId));
  }, [users, activityTypes]);

  const openRowDetail = useCallback((row: ActivityBoardItem) => {
    setDetailRow(row);
    setOpenDetail(true);
  }, []);

  const closeRowDetail = useCallback(() => {
    setOpenDetail(false);
    setDetailRow(null);
  }, []);

  // CRUD dialog handlers
  const handleClickOpenCreateDialog = () => setOpenCreateDialog(true);
  const handleCloseCreateDialog = () => setOpenCreateDialog(false);

  const handleClickOpenUpdateDialog = () => {
    const selectedRowId = requireSingleRowSelection(rowSelectionModel);
    if (selectedRowId === null) return;

    const isAdmin = permissionCodes.includes('update-activity-board-item-admin');
    const selectedRow = activityBoardItems.find((row) => row.id === selectedRowId);
    const activityType = activityTypes.find((at) => at.id === selectedRow?.activityTypeId);
    const isPermissionActivity = activityType?.isPermissionActivity === true;

    if (isPermissionActivity && !isAdmin) {
      enqueueSnackbar(
        <FormattedMessage
          id="activityManaging.isPermissionActivityUpdateAdminRequired"
          defaultMessage="İzin aktivitesini güncellemek için admin yetkisine sahip olmalısınız!"
        />,
        {
          variant: 'warning',
        }
      );
      return;
    }

    if (!isAdmin) {
      const meId = users.find((user) => user.nameSurname === Authentication.getLoggedUser())?.id;

      if (selectedRow?.assignedUserId !== meId) {
        enqueueSnackbar(
          <FormattedMessage
            id="activityManaging.notOwnerUpdate"
            defaultMessage="Yalnızca tarafınıza ait aktiviteleri güncelleyebilirsiniz!"
          />,
          {
            variant: 'warning',
          }
        );
        return;
      }
    }

    setOpenUpdateDialog(true);
  };
  const handleCloseUpdateDialog = () => setOpenUpdateDialog(false);

  const handleClickOpenDeleteDialog = () => {
    const selectedRowId = requireSingleRowSelection(rowSelectionModel);
    if (selectedRowId === null) return;

    const isAdmin = permissionCodes.includes('delete-activity-board-item-admin');
    const selectedRow = activityBoardItems.find((row) => row.id === selectedRowId);
    const activityType = activityTypes.find((at) => at.id === selectedRow?.activityTypeId);
    const isPermissionActivity = activityType?.isPermissionActivity === true;

    if (isPermissionActivity && !isAdmin) {
      enqueueSnackbar(
        <FormattedMessage
          id="activityManaging.isPermissionActivityDeleteAdminRequired"
          defaultMessage="İzin aktivitesini silmek için admin yetkisine sahip olmalısınız!"
        />,
        {
          variant: 'warning',
        }
      );
      return;
    }

    if (!isAdmin) {
      const meId = users.find((user) => user.nameSurname === Authentication.getLoggedUser())?.id;

      if (selectedRow?.assignedUserId !== meId) {
        enqueueSnackbar(
          <FormattedMessage
            id="activityManaging.notOwnerDelete"
            defaultMessage="Yalnızca tarafınıza ait aktiviteleri silebilirsiniz!"
          />,
          {
            variant: 'warning',
          }
        );
        return;
      }
    }

    setOpenDeleteDialog(true);
  };
  const handleCloseDeleteDialog = () => setOpenDeleteDialog(false);

  const columns: GridColDef<ActivityBoardItem>[] = useMemo(
    () => [
      {
        field: 'actions',
        type: 'actions',
        headerName: intl.formatMessage({ id: 'common.detail', defaultMessage: 'Detay' }),
        width: 120,
        getActions: (params) => {
          if (isAutogeneratedRow(params.row)) {
            return [];
          }

          return [
            <GridActionsCellItem
              key="detail"
              icon={<VisibilityOutlinedIcon sx={{ color: 'primary.main', opacity: 0.85 }} />}
              label={intl.formatMessage({ id: 'common.detail', defaultMessage: 'Detay' })}
              onClick={() => openRowDetail(params.row as ActivityBoardItem)}
              showInMenu={false}
            />,
          ];
        },
      },
      {
        field: 'bookId',
        headerName: intl.formatMessage({ id: 'activityManaging.book', defaultMessage: 'Proje' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => books.filter((book): book is Book => !!book && book.id != null).map((book) => book.id),
        getOptionLabel: (value) => books.find((book) => String(book.id) === String(value))?.companyName ?? '',
        valueFormatter: (value) => books.find((book) => String(book.id) === String(value))?.companyName ?? '',
        minWidth: 200,
      },
      {
        field: 'projectId',
        headerName: intl.formatMessage({ id: 'activityManaging.product', defaultMessage: 'Ürün' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () =>
          products
            .filter((project): project is Project => !!project && project.id != null)
            .map((project) => project.id),
        getOptionLabel: (value) => products.find((project) => String(project.id) === String(value))?.title ?? '',
        valueFormatter: (value) => products.find((project) => String(project.id) === String(value))?.title ?? '',
        minWidth: 200,
      },
      {
        field: 'assignedUserId',
        headerName: intl.formatMessage({ id: 'activityManaging.assignedUser', defaultMessage: 'Atanan Kullanıcı' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => users.find((user) => user.id === value)?.nameSurname || '',
        minWidth: 200,
      },
      {
        field: 'activityTypeId',
        headerName: intl.formatMessage({ id: 'activityManaging.activityType', defaultMessage: 'Aktivite Türü' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => activityTypes.find((at) => at.id === value)?.title || '',
        minWidth: 200,
      },
      {
        field: 'effortTrackNo',
        headerName: intl.formatMessage({ id: 'activityManaging.effortTrackNo', defaultMessage: 'Efor Takip No' }),
        headerAlign: 'center',
        align: 'center',
        minWidth: 250,
      },
      {
        field: 'description',
        headerName: intl.formatMessage({ id: 'activityManaging.description', defaultMessage: 'Açıklama' }),
        minWidth: 250,
      },
      {
        field: 'startDate',
        ...dateColumnType,
        headerName: intl.formatMessage({ id: 'activityManaging.startDate', defaultMessage: 'Başlangıç Tarihi' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => (!value ? null : new Date(value)),
        valueParser: (value) => (!value ? null : new Date(value)),
        valueFormatter: (value) => (value ? renderFormattedDate(value, true) : ''),
        minWidth: 150,
      },
      {
        field: 'endDate',
        ...dateColumnType,
        headerName: intl.formatMessage({ id: 'activityManaging.endDate', defaultMessage: 'Bitiş Tarihi' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => (!value ? null : new Date(value)),
        valueParser: (value) => (!value ? null : new Date(value)),
        valueFormatter: (value) => (value ? renderFormattedDate(value, true) : ''),
        minWidth: 150,
      },
      {
        field: 'estimatedHours',
        headerName: intl.formatMessage({
          id: 'activityManaging.estimatedHours',
          defaultMessage: 'Harcanan Süre (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        minWidth: 150,
      },
      {
        field: 'isOvertimeWork',
        headerName: intl.formatMessage({ id: 'activityManaging.isOvertimeWork', defaultMessage: 'Fazla Mesai' }),
        headerAlign: 'center',
        type: 'boolean',
        minWidth: 130,
      },
    ],
    [intl, books, products, users, activityTypes, openRowDetail]
  );

  const handleDateChipChange = useCallback((key: string, range: DateRangeFilter) => {
    setSelectedDateChipKey(key);
    setDateRange(range);
  }, []);

  const getActivityBoardItems = useCallback(async () => {
    const requestId = ++activityBoardItemsRequestIdRef.current;

    try {
      setLoading(true);

      const response = await apiService.getActivityBoardItems({
        bookId: selectedBook?.id || null,
        projectId: selectedProduct?.id || null,
        userId: selectedUser?.id || null,
        startDate: dateRange.startedDate,
        endDate: dateRange.endDate,
      });

      if (requestId === activityBoardItemsRequestIdRef.current && response.isSuccess) {
        const sortedData = [...response.data].sort(
          (a, b) => new Date(b.startDate ?? 0).getTime() - new Date(a.startDate ?? 0).getTime()
        );
        setActivityBoardItems(sortedData);
        setRowSelectionModel([]);
      }
    } catch {
    } finally {
      if (requestId === activityBoardItemsRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [selectedBook, selectedProduct, selectedUser, dateRange]);

  // useEffect hooks
  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [products, isEmptyProducts, dispatch]);

  useEffect(() => {
    getActivityBoardItems();

    return () => {
      activityBoardItemsRequestIdRef.current += 1;
    };
  }, [getActivityBoardItems]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-activity-board-item']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2.5 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateDialog}>
              <FormattedMessage id="activityManaging.createActivityBoardItem" defaultMessage="Aktivite Oluştur" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>

        <PermissionHandler requiredPermissionCodes={['update-activity-board-item', 'update-activity-board-item-admin']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2.5 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateDialog} color="info">
              <FormattedMessage id="activityManaging.updateActivityBoardItem" defaultMessage="Aktivite Güncelle" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>

        <PermissionHandler requiredPermissionCodes={['delete-activity-board-item', 'delete-activity-board-item-admin']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2.5 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteDialog}>
              <FormattedMessage id="activityManaging.deleteActivityBoardItem" defaultMessage="Aktivite Sil" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>

        {showMonthEndOvertimeReminder && (
          <Grid size={{ xs: 12 }}>
            <Alert severity="warning" variant="outlined" sx={{ width: '100%', alignItems: 'center', mt: 1 }}>
              <FormattedMessage
                id="activityManaging.monthEndOvertimeReminder"
                defaultMessage="Bugün ayın son günü. İçinde bulunduğumuz aya ait Fazla Mesai aktivitelerini ay kapanmadan girmeniz gerekir."
              />
            </Alert>
          </Grid>
        )}
      </Grid>

      <DateRangeChipsArray selectedChipKey={selectedDateChipKey} onChipChange={handleDateChipChange} />

      <DataGridBox>
        <ZebraDataGrid<ActivityBoardItem>
          pagination
          checkboxSelection
          rowSelectionPropagation={{
            parents: false,
            descendants: false,
          }}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel((prev) => {
              const prevStr = JSON.stringify(prev);
              const nextStr = JSON.stringify(newRowSelectionModel);

              return prevStr === nextStr ? prev : newRowSelectionModel;
            });
          }}
          rowSelectionModel={rowSelectionModel}
          columns={columns}
          initialState={{
            aggregation: {
              model: {
                estimatedHours: 'sum',
              },
            },
          }}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={activityBoardItems}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              exportPremiumExcel: true,
              showTemplates: true,
              apiRef,
              pageName,
              singleFilter: true,
              singleFilterComponent: (
                <ToolbarFilterRow>
                  <Autocomplete
                    size="small"
                    sx={{ width: { xs: '100%', sm: 160, xl: 180 }, minWidth: 0, maxWidth: '100%' }}
                    options={books}
                    value={selectedBook}
                    {...renderAutoCompleteLocaleText(intl)}
                    onChange={(_, newValue: Book | null) => setSelectedBook(newValue)}
                    getOptionLabel={(option: Book) => option.companyName ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label={<FormattedMessage id="activityManaging.book" defaultMessage="Proje" />}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  <Autocomplete
                    size="small"
                    sx={{ width: { xs: '100%', sm: 160, xl: 180 }, minWidth: 0, maxWidth: '100%' }}
                    options={products}
                    value={selectedProduct}
                    {...renderAutoCompleteLocaleText(intl)}
                    onChange={(_, newValue: Project | null) => setSelectedProduct(newValue)}
                    getOptionLabel={(option: Project) => option.title ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label={<FormattedMessage id="activityManaging.product" defaultMessage="Ürün" />}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  <PermissionHandler requiredPermissionCodes={['list-activity-board-item-admin']}>
                    <Autocomplete
                      size="small"
                      sx={{ width: { xs: '100%', sm: 160, xl: 180 }, minWidth: 0, maxWidth: '100%' }}
                      options={users}
                      value={selectedUser}
                      {...renderAutoCompleteLocaleText(intl)}
                      onChange={(_, newValue: User | null) => setSelectedUser(newValue)}
                      getOptionLabel={(option: User) => option.nameSurname ?? ''}
                      slotProps={{
                        paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label={
                            <FormattedMessage id="activityManaging.assignedUser" defaultMessage="Atanan Kullanıcı" />
                          }
                          sx={{
                            '& .MuiButtonBase-root': {
                              border: 'none',
                              backgroundColor: 'inherit',
                              width: '2.25rem',
                              height: '2.25rem',
                              padding: '0.25rem',
                              [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </PermissionHandler>
                </ToolbarFilterRow>
              ),
            },
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
              },
            },
          }}
          disableRowGrouping
          apiRef={apiRef}
        />
      </DataGridBox>

      <CreateActivityBoardItemDialog
        open={openCreateDialog}
        handleClose={handleCloseCreateDialog}
        books={books}
        products={products}
        activityTypes={filteredActivityTypes}
        users={users}
        refresh={getActivityBoardItems}
      />
      <UpdateActivityBoardItemDialog
        open={openUpdateDialog}
        handleClose={handleCloseUpdateDialog}
        selectedRow={selectedRow}
        books={books}
        products={products}
        activityTypes={filteredActivityTypes}
        users={users}
        refresh={getActivityBoardItems}
      />
      <DeleteActivityBoardItemDialog
        open={openDeleteDialog}
        activityBoardItemId={selectedRow?.id ?? null}
        handleClose={handleCloseDeleteDialog}
        refresh={getActivityBoardItems}
        setRowSelectionModel={setRowSelectionModel}
      />
      <DetailDrawer
        open={openDetail}
        close={closeRowDetail}
        detailRow={detailRow}
        users={users}
        books={books}
        products={products}
        activityTypes={activityTypes}
      />
    </>
  );
};

export default ActivityManaging;
